import {
  BASKERVILLE,
  BASKERVILLE_BOLD,
  CARLITO,
  NOTO_SERIF,
  NOTO_SERIF_BOLD,
  PINYON,
  type CodeRanges,
} from './coverage'
import {FACES, SCRIPT_FACES, type CertificateFace} from './fonts'
import type {CertificateFontStack} from './layouts'

const SCRIPT_LOOK_ALIKES = /[ƁɓƊɗƘƙƳƴ]/

const FORMAT_CHARACTER = /\p{Cf}/u

const STRONG_LTR = /^[\p{Lu}\p{Ll}\p{Lo}\p{Lt}\p{Lm}\p{Nd}]/u

type GraphemeSegmenter = {segment(text: string): Iterable<{segment: string}>}

const segmenter: GraphemeSegmenter | null = (() => {
  const Segmenter = (
    Intl as unknown as {Segmenter?: new (locale?: string, options?: object) => GraphemeSegmenter}
  ).Segmenter
  return Segmenter ? new Segmenter(undefined, {granularity: 'grapheme'}) : null
})()

export function prepareText(text: string): string {
  return text.normalize('NFC').replace(/ʻ/g, '‘').replace(/ʼ/g, '’')
}

export function clusters(text: string): string[] {
  return segmenter ? Array.from(segmenter.segment(text), (item) => item.segment) : Array.from(text)
}

function inRanges(codePoint: number, ranges: CodeRanges): boolean {
  let low = 0
  let high = ranges.length - 1
  while (low <= high) {
    const middle = (low + high) >> 1
    const [start, end] = ranges[middle]
    if (codePoint < start) {
      high = middle - 1
    } else if (codePoint > end) {
      low = middle + 1
    } else {
      return true
    }
  }
  return false
}

function covers(ranges: CodeRanges, cluster: string): boolean {
  return Array.from(cluster)
    .filter((character) => !FORMAT_CHARACTER.test(character))
    .every((character) => inRanges(character.codePointAt(0)!, ranges))
}

function isScriptSafe(text: string): boolean {
  return (
    !SCRIPT_LOOK_ALIKES.test(text) && clusters(text).every((cluster) => covers(PINYON, cluster))
  )
}

function withFallbacks(
  text: string,
  preferred: CertificateFace,
  preferredRanges: CodeRanges,
  fallback: CertificateFace,
  fallbackRanges: CodeRanges
): CertificateFace[] {
  const useFallback = clusters(text).some(
    (cluster) => covers(fallbackRanges, cluster) && !covers(preferredRanges, cluster)
  )
  return [useFallback ? fallback : preferred, ...SCRIPT_FACES]
}

export function faceStack(stack: CertificateFontStack, text: string): CertificateFace[] {
  switch (stack) {
    case 'name':
      return isScriptSafe(text)
        ? [FACES.pinyon]
        : withFallbacks(text, FACES.baskerville, BASKERVILLE, FACES.notoSerif, NOTO_SERIF)
    case 'serif':
      return withFallbacks(text, FACES.baskerville, BASKERVILLE, FACES.notoSerif, NOTO_SERIF)
    case 'serifBold':
      return withFallbacks(
        text,
        FACES.baskervilleBold,
        BASKERVILLE_BOLD,
        FACES.notoSerifBold,
        NOTO_SERIF_BOLD
      )
    case 'sans':
      return withFallbacks(text, FACES.carlito, CARLITO, FACES.notoSerif, NOTO_SERIF)
  }
}

function strongDirection(cluster: string): CanvasDirection | null {
  const codePoint = cluster.codePointAt(0)!
  if (
    (codePoint >= 0x0590 && codePoint <= 0x08ff) ||
    (codePoint >= 0xfb1d && codePoint <= 0xfdff) ||
    (codePoint >= 0xfe70 && codePoint <= 0xfeff)
  ) {
    return 'rtl'
  }
  return STRONG_LTR.test(cluster) ? 'ltr' : null
}

export function paragraphDirection(text: string): CanvasDirection {
  for (const cluster of clusters(text)) {
    const direction = strongDirection(cluster)
    if (direction) {
      return direction
    }
  }
  return 'ltr'
}
